<template>
  <ContentWrap>
    <el-alert type="info" :closable="false" show-icon class="mb-12px" title="公章用印：草稿 → 提交 → 审批 → 确认用印。列表仅本所案关联申请。" />
    <el-form :inline="true" class="mb-12px">
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-120px">
          <el-option label="草稿" value="DRAFT" />
          <el-option label="待审" value="PENDING" />
          <el-option label="已通过" value="APPROVED" />
          <el-option label="已用印" value="USED" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button type="success" @click="openCreate">新建申请</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="reason" label="事由" min-width="140" show-overflow-tooltip />
      <el-table-column prop="fileName" label="文件" min-width="120" show-overflow-tooltip />
      <el-table-column prop="copies" label="份数" width="70" />
      <el-table-column prop="firmCaseNo" label="案号" min-width="110" />
      <el-table-column prop="status" label="状态" width="100" />
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button v-if="row.status === 'DRAFT'" link type="primary" @click="doSubmit(row)">提交</el-button>
          <el-button v-if="row.status === 'PENDING'" link type="success" @click="doApprove(row, true)">通过</el-button>
          <el-button v-if="row.status === 'PENDING'" link type="danger" @click="doApprove(row, false)">驳回</el-button>
          <el-button v-if="row.status === 'APPROVED'" link type="warning" @click="doConfirm(row)">确认用印</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination v-model:page="query.pageNo" v-model:limit="query.pageSize" :total="total" @pagination="getList" />
    <Dialog v-model="visible" title="用印申请" width="480px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="事由" prop="reason"><el-input v-model="form.reason" /></el-form-item>
        <el-form-item label="文件名" prop="fileName"><el-input v-model="form.fileName" /></el-form-item>
        <el-form-item label="份数" prop="copies"><el-input-number v-model="form.copies" :min="1" /></el-form-item>
        <el-form-item label="关联案件" prop="caseId">
          <el-select v-model="form.caseId" filterable class="!w-100%" placeholder="须关联本所 Matter">
            <el-option v-for="m in matters" :key="m.id" :label="`${m.firmCaseNo || m.id} · ${m.title}`" :value="m.id!" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存草稿</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>
<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { FirmOfficeApi, type PracticeSealVO } from '@/api/firm/office'
import { FirmPracticeApi, type PracticeMatterVO } from '@/api/firm/practice'
defineOptions({ name: 'FirmPracticeSeal' })
const message = useMessage()
const loading = ref(false)
const list = ref<PracticeSealVO[]>([])
const total = ref(0)
const query = reactive({ pageNo: 1, pageSize: 10, status: undefined as string | undefined })
const matters = ref<PracticeMatterVO[]>([])
const visible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({ reason: '', fileName: '', copies: 1, caseId: undefined as number | undefined })
const rules: FormRules = {
  reason: [{ required: true, message: '必填', trigger: 'blur' }],
  fileName: [{ required: true, message: '必填', trigger: 'blur' }],
  copies: [{ required: true, message: '必填', trigger: 'blur' }],
  caseId: [{ required: true, message: '请选择本所案件', trigger: 'change' }]
}
const getList = async () => {
  loading.value = true
  try {
    const data = await FirmOfficeApi.sealPage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}
const handleQuery = () => { query.pageNo = 1; getList() }
const openCreate = async () => {
  Object.assign(form, { reason: '', fileName: '', copies: 1, caseId: undefined })
  visible.value = true
  const m = await FirmPracticeApi.matterPage({ pageNo: 1, pageSize: 100 })
  matters.value = m?.list || []
}
const submit = async () => {
  await formRef.value?.validate()
  if (!form.caseId) return
  saving.value = true
  try {
    await FirmOfficeApi.sealCreate({
      reason: form.reason.trim(),
      fileName: form.fileName.trim(),
      copies: form.copies,
      caseId: form.caseId
    })
    message.success('已创建')
    visible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}
const doSubmit = async (row: PracticeSealVO) => {
  if (!row.id) return
  await FirmOfficeApi.sealSubmit(row.id)
  message.success('已提交')
  await getList()
}
const doApprove = async (row: PracticeSealVO, approved: boolean) => {
  if (!row.id) return
  const remark = approved ? undefined : window.prompt('驳回意见') || undefined
  if (!approved && !remark) return
  await FirmOfficeApi.sealApprove({ id: row.id, approved, remark })
  message.success(approved ? '已通过' : '已驳回')
  await getList()
}
const doConfirm = async (row: PracticeSealVO) => {
  if (!row.id) return
  await FirmOfficeApi.sealConfirmUse({ id: row.id })
  message.success('已确认用印')
  await getList()
}
onMounted(getList)
</script>
